import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AppBar,
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    LinearProgress,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Portal,
    Select,
    Snackbar,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Toolbar,
    Tooltip,
    Typography,
    Switch,
    useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_URL as API_BASE } from "../config";
import PageHeader from "../components/ui/PageHeader";
import { panelCardSx } from "../theme/surfaceStyles";
import { useAuth } from "../auth/AuthContext";
import {
    PLANNER_CHILD_LINE_KINDS,
    PLANNER_KINDS,
    PLANNER_PRIORITIES,
    PLANNER_SKELETONS,
    PLANNER_STATUSES,
    PLANNER_TONES,
    plannerKindAccent,
} from "./planner/plannerTopics";
import { LABOR_CATEGORIES } from "./planner/plannerRates";
import { itemQuotePreview, lineKindOf, PURCHASE_TREATMENTS } from "./planner/plannerQuote";
import WixJobQuotePanel from "./quotes/WixJobQuotePanel";
import { buildWixJobQuote } from "./quotes/wixJobQuote";
import JobLifecycleBar from "./planner/JobLifecycleBar";
import LogJobActionDialog from "./planner/LogJobActionDialog";
import {
    jobQuoteLocked,
    quoteRevLabel,
    quoteStateColor,
    quoteStateLabel,
} from "./planner/plannerQuoteState";

const PLANNER_API = `${API_BASE}/planner`;

const FILTERS = [
    { id: "open", label: "All open" },
    { id: "attention", label: "Needs attention" },
    { id: "customer", label: "Customer" },
    { id: "general", label: "General" },
    { id: "archived", label: "Archived" },
];

const STATUS_COLOR = {
    idea: "default",
    need: "warning",
    quoted: "info",
    doing: "success",
    blocked: "error",
    done: "success",
    dropped: "default",
};

const CHILD_SECTIONS = [
    { id: "component", label: "Components" },
    { id: "labor", label: "Labor" },
    { id: "purchase", label: "Purchases" },
];

function kindSurfaceSx(theme, kind, { hover = false, filled } = {}) {
    const accent = plannerKindAccent(theme, kind);
    const useFill = filled ?? kind !== "component";
    const fill = alpha(accent, theme.palette.mode === "dark" ? 0.14 : 0.08);
    return {
        bgcolor: useFill ? fill : theme.palette.background.paper,
        border: `1px solid ${alpha(accent, useFill ? 0.38 : 0.22)}`,
        borderLeft: `4px solid ${accent}`,
        ...(hover
            ? {
                "&:hover": {
                    borderColor: alpha(accent, 0.6),
                    boxShadow: `0 4px 18px ${alpha(accent, theme.palette.mode === "dark" ? 0.18 : 0.12)}`,
                },
            }
            : {}),
    };
}

function blankItem(overrides = {}) {
    return {
        id: "",
        title: "",
        body: "",
        groupKey: "",
        parentId: "",
        customerId: "",
        customerName: "",
        customerEmail: "",
        scope: "general",
        status: "idea",
        priority: "normal",
        kind: "need",
        lineKind: "job",
        laborCategory: "",
        budgetTarget: "",
        qty: "",
        unitCost: "",
        purchaseTreatment: "pbr",
        hideFromCustomer: false,
        contingencyPct: "",
        nextAction: "",
        dueDate: "",
        leadTime: "",
        laborEstimate: "",
        costEstimate: "",
        purchaseEstimate: "",
        purchaseNotes: "",
        tags: "",
        pinned: false,
        archived: false,
        ...overrides,
    };
}

function blankGroup() {
    return { id: "", key: "", label: "", blurb: "", icon: "◎", tone: "lime", sortOrder: "", archived: false };
}

function statusLabel(id) {
    return PLANNER_STATUSES.find((row) => row.id === id)?.label || id;
}

function priorityLabel(id) {
    return PLANNER_PRIORITIES.find((row) => row.id === id)?.label || id;
}

function lineKindLabel(kind) {
    const all = [{ id: "job", label: "Job" }, ...PLANNER_CHILD_LINE_KINDS];
    return all.find((row) => row.id === kind)?.label || kind;
}

function matchesFilter(item, filter) {
    if (filter === "archived") return Boolean(item.archived);
    if (item.archived) return false;
    if (filter === "open") return item.open !== false && item.status !== "done" && item.status !== "dropped";
    if (filter === "customer") return item.scope === "customer";
    if (filter === "general") return item.scope === "general";
    if (filter === "attention") {
        return (
            Boolean(item.overdue) ||
            Boolean(item.dueSoon) ||
            item.status === "blocked" ||
            item.priority === "urgent" ||
            Boolean(item.quoteDirty)
        );
    }
    return true;
}

function childMap(items) {
    const map = new Map();
    for (const item of items || []) {
        const key = item.parentId || "";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(item);
    }
    return map;
}

function rootIdOf(item, lookup) {
    let current = item;
    const seen = new Set();
    while (current?.parentId && lookup.has(current.parentId) && !seen.has(current.id)) {
        seen.add(current.id);
        current = lookup.get(current.parentId);
    }
    return current?.id || item?.id || "";
}

function crumbPath(item, lookup) {
    const path = [];
    let current = item;
    const seen = new Set();
    while (current && !seen.has(current.id)) {
        seen.add(current.id);
        path.unshift(current);
        current = current.parentId ? lookup.get(current.parentId) : null;
    }
    return path;
}

function formatMoney(value) {
    if (value == null || value === "") return "";
    const number = Number(value);
    if (!Number.isFinite(number)) return "";
    return `$${number.toLocaleString(undefined, { maximumFractionDigits: number % 1 ? 2 : 0 })}`;
}

function formatHours(value) {
    if (value == null || value === "") return "";
    const number = Number(value);
    if (!Number.isFinite(number)) return "";
    const label = number === 1 ? "hr" : "hrs";
    return `${number.toLocaleString(undefined, { maximumFractionDigits: number % 1 ? 1 : 0 })} ${label}`;
}

function defaultTitleForKind(lineKind, laborCategory) {
    if (lineKind === "labor") {
        return LABOR_CATEGORIES.find((row) => row.id === laborCategory)?.label || "Labor";
    }
    if (lineKind === "purchase") return "Purchases / materials";
    return "";
}

function childQuoteOf(item) {
    if (!item?.id) return null;
    return {
        laborHours: item.quoteChildLaborHours,
        cost: item.quoteChildCost,
        purchases: item.quoteChildPurchases,
        customerPurchases: item.quoteChildCustomerPurchases,
        total: item.quoteChildTotal,
        estimate: item.quoteChildEstimates,
    };
}

function Completion({ done = 0, total = 0, empty = "No work tracked" }) {
    if (!total) {
        return (
            <Typography variant="caption" color="text.secondary">
                {empty}
            </Typography>
        );
    }
    const pct = Math.round((done / total) * 100);
    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 110 }}>
            <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 6, borderRadius: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                {pct}% · {done}/{total}
            </Typography>
        </Stack>
    );
}

function StatusChip({ status }) {
    return <Chip size="small" label={statusLabel(status)} color={STATUS_COLOR[status] || "default"} />;
}

function LineKindChip({ kind }) {
    return (
        <Chip
            size="small"
            label={lineKindLabel(kind)}
            sx={(theme) => {
                const accent = plannerKindAccent(theme, kind);
                const filled = kind !== "component";
                return filled
                    ? {
                        bgcolor: alpha(accent, 0.22),
                        color: accent,
                        border: `1px solid ${alpha(accent, 0.45)}`,
                        fontWeight: 800,
                    }
                    : {
                        bgcolor: "transparent",
                        color: accent,
                        border: `1px solid ${alpha(accent, 0.45)}`,
                        fontWeight: 800,
                    };
            }}
        />
    );
}

function FactChip({ label, value, color = "default" }) {
    if (value == null || value === "") return null;
    return <Chip size="small" variant="outlined" color={color} label={`${label}: ${value}`} />;
}

function laborCategoryLabel(item) {
    const id = item.laborCategory || item.quoteLaborCategory;
    return LABOR_CATEGORIES.find((row) => row.id === id)?.label || item.quoteRateLabel || "";
}

function purchaseTreatmentLabel(item) {
    const id = item.purchaseTreatment || "pbr";
    return PURCHASE_TREATMENTS.find((row) => row.id === id)?.label || "";
}

function ItemKindDetails({ item, kind }) {
    if (kind === "labor") {
        const hours = formatHours(item.quoteOwnLaborHours || item.laborEstimate);
        const rate = item.quoteHourlyRate ? `$${item.quoteHourlyRate}/hr` : "";
        const cost = formatMoney(item.quoteOwnCost || item.costEstimate || item.quoteRollupTotal);
        const category = laborCategoryLabel(item);
        return (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {category ? <Chip size="small" variant="outlined" label={category} /> : null}
                {hours ? <Chip size="small" variant="outlined" label={`${hours} quoted`} /> : null}
                {item.actualHours ? (
                    <Chip
                        size="small"
                        color={Number(item.actualHours) > Number(item.quoteOwnLaborHours || item.laborEstimate || 0) ? "warning" : "success"}
                        variant="outlined"
                        label={`${formatHours(item.actualHours)} logged`}
                    />
                ) : null}
                {rate ? <Chip size="small" variant="outlined" label={rate} /> : null}
                {cost ? <Chip size="small" variant="outlined" label={cost} /> : null}
                {item.nextAction ? (
                    <Typography variant="caption" color="text.secondary">
                        Next: {item.nextAction}
                    </Typography>
                ) : null}
            </Stack>
        );
    }
    if (kind === "purchase") {
        const qty = item.qty ? `Qty ${item.qty}` : "";
        const unit = item.unitCost ? `${formatMoney(item.unitCost)} each` : "";
        const total = formatMoney(item.quoteOwnPurchases || item.purchaseEstimate || item.quoteRollupTotal);
        const treatment = purchaseTreatmentLabel(item);
        const notes = String(item.purchaseNotes || "").trim();
        return (
            <Stack spacing={0.5}>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {treatment ? <Chip size="small" variant="outlined" label={treatment} /> : null}
                    {qty ? <Chip size="small" variant="outlined" label={qty} /> : null}
                    {unit ? <Chip size="small" variant="outlined" label={unit} /> : null}
                    {total ? <Chip size="small" variant="outlined" label={total} /> : null}
                </Stack>
                {notes ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {notes}
                    </Typography>
                ) : null}
            </Stack>
        );
    }
    return null;
}

function ItemCard({ item, childCount = 0, onOpen, onEdit, onLogTime, dense = false }) {
    const kind = lineKindOf(item);
    return (
        <Paper
            sx={(theme) => ({
                ...panelCardSx(theme, { hover: true }),
                ...kindSurfaceSx(theme, kind, { hover: true }),
                display: "flex",
                alignItems: "stretch",
                overflow: "hidden",
            })}
        >
            <Box
                onClick={() => onOpen(item)}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    px: dense ? 1.5 : 2,
                    py: dense ? 1.25 : 1.75,
                    cursor: "pointer",
                    minHeight: 56,
                }}
            >
                <Stack spacing={0.75}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <LineKindChip kind={kind} />
                        <StatusChip status={item.status} />
                        {item.overdue ? <Chip size="small" color="error" label="Overdue" /> : null}
                        {item.dueSoon && !item.overdue ? <Chip size="small" color="warning" label="Due soon" /> : null}
                        {item.priority === "urgent" || item.priority === "high" ? (
                            <Chip
                                size="small"
                                variant="outlined"
                                color={item.priority === "urgent" ? "error" : "warning"}
                                label={priorityLabel(item.priority)}
                            />
                        ) : null}
                        {item.hideFromCustomer ? (
                            <Chip size="small" color="warning" variant="outlined" label="Internal only" />
                        ) : null}
                        {!item.parentId ? (
                            <Chip
                                size="small"
                                color={quoteStateColor(item.quoteState)}
                                variant={jobQuoteLocked(item) ? "filled" : "outlined"}
                                label={`${quoteStateLabel(item.quoteState)} · ${quoteRevLabel(item.quoteRev)}`}
                            />
                        ) : null}
                        {item.quoteDirty ? <Chip size="small" color="warning" label="Quote stale" /> : null}
                    </Stack>
                    <Typography variant={dense ? "body1" : "subtitle1"} sx={{ fontWeight: 800, lineHeight: 1.3 }}>
                        {item.pinned ? "★ " : ""}
                        {item.title}
                    </Typography>
                    {kind === "labor" || kind === "purchase" ? (
                        <ItemKindDetails item={item} kind={kind} />
                    ) : (
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            {(item.quoteRollupTotal != null || item.quoteEstimate != null) && (
                                <Typography variant="caption" color="text.secondary">
                                    {formatMoney(item.quoteRollupTotal) || "—"}
                                    {item.quoteEstimate != null ? ` / ${formatMoney(item.quoteEstimate)}` : ""}
                                </Typography>
                            )}
                            {childCount ? (
                                <Typography variant="caption" color="text.secondary">
                                    {childCount} child{childCount === 1 ? "" : "ren"}
                                </Typography>
                            ) : null}
                        </Stack>
                    )}
                    <Completion done={item.workDone ?? item.rollupDone} total={item.workTotal ?? item.rollupTotal} />
                </Stack>
            </Box>
            <Stack
                justifyContent="center"
                alignItems="center"
                sx={(theme) => ({
                    pr: 0.5,
                    py: 0.5,
                    borderLeft: 1,
                    borderColor: "divider",
                    bgcolor: kind === "component"
                        ? "transparent"
                        : alpha(plannerKindAccent(theme, kind), theme.palette.mode === "dark" ? 0.18 : 0.12),
                })}
            >
                <Tooltip title="Edit">
                    <IconButton
                        color="primary"
                        onClick={(event) => {
                            event.stopPropagation();
                            onEdit(item);
                        }}
                        aria-label={`Edit ${item.title}`}
                        sx={{ width: 44, height: 44 }}
                    >
                        <EditIcon />
                    </IconButton>
                </Tooltip>
                {kind === "labor" && onLogTime ? (
                    <Tooltip title="Log time">
                        <IconButton
                            color="primary"
                            onClick={(event) => {
                                event.stopPropagation();
                                onLogTime(item);
                            }}
                            aria-label={`Log time on ${item.title}`}
                            sx={{ width: 44, height: 44 }}
                        >
                            <MoreTimeIcon />
                        </IconButton>
                    </Tooltip>
                ) : null}
                <IconButton
                    onClick={() => onOpen(item)}
                    aria-label={`Open ${item.title}`}
                    sx={{ width: 44, height: 44 }}
                >
                    <ChevronRightIcon />
                </IconButton>
            </Stack>
        </Paper>
    );
}

export default function JobPlanner() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [groups, setGroups] = useState([]);
    const [items, setItems] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [pricingSettings, setPricingSettings] = useState({ material_markup_percent: 12 });

    const [filter, setFilter] = useState("open");
    const [query, setQuery] = useState("");

    const [draft, setDraft] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [groupDraft, setGroupDraft] = useState(null);
    const [groupsOpen, setGroupsOpen] = useState(false);
    const [newJobStep, setNewJobStep] = useState("");
    const [customerQuery, setCustomerQuery] = useState("");
    const [checkTitle, setCheckTitle] = useState("");
    const [confirm, setConfirm] = useState(null);
    const [skeletonAnchor, setSkeletonAnchor] = useState(null);
    const [addAnchor, setAddAnchor] = useState(null);
    const [moreAnchor, setMoreAnchor] = useState(null);
    const [logOpen, setLogOpen] = useState(false);
    const [logKind, setLogKind] = useState("time");
    const [logItemId, setLogItemId] = useState("");

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [snackbar, setSnack] = useState({ open: false, message: "", severity: "success" });

    const toast = useCallback((message, severity = "success") => {
        setSnack({ open: true, message, severity });
    }, []);

    const applyBundle = useCallback((payload) => {
        if (!payload) return;
        setGroups(payload.groups || []);
        setItems(payload.items || []);
        if (payload.customers) setCustomers(payload.customers);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(PLANNER_API, { withCredentials: true });
            applyBundle(res.data);
        } catch (err) {
            toast(err.response?.data?.error || err.message || "Unable to load planner.", "error");
        } finally {
            setLoading(false);
        }
    }, [applyBundle, toast]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        axios
            .get(`${API_BASE}/pricing`, { withCredentials: true })
            .then((res) => setPricingSettings(res.data?.settings || { material_markup_percent: 12 }))
            .catch(() => { });
    }, []);

    const customerIdParam = searchParams.get("customerId") || "";
    useEffect(() => {
        if (!customerIdParam) return;
        setFilter("customer");
    }, [customerIdParam]);

    const updateNav = useCallback(
        (nextJob, nextItem, { replace = false } = {}) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (nextJob) next.set("job", nextJob);
                    else next.delete("job");
                    if (nextItem && nextItem !== nextJob) next.set("item", nextItem);
                    else next.delete("item");
                    return next;
                },
                { replace }
            );
        },
        [setSearchParams]
    );

    async function act(action, extra = {}) {
        const { keepDraft = true, quiet = false, navigateToItem = false, ...body } = extra;
        if (!quiet) {
            setBusy(true);
        }
        try {
            const res = await axios.post(
                PLANNER_API,
                { action, ...body },
                { withCredentials: true }
            );
            const payload = res.data || {};
            applyBundle(payload);
            setDraft((prev) => {
                if (payload.item && keepDraft !== false) {
                    return (payload.items || []).find((row) => row.id === payload.item.id) || payload.item;
                }
                if (!prev?.id) return prev;
                return (payload.items || []).find((row) => row.id === prev.id) || prev;
            });
            if (navigateToItem && payload.item?.id) {
                const lookup = new Map((payload.items || []).map((row) => [row.id, row]));
                updateNav(rootIdOf(payload.item, lookup), payload.item.id);
            }
            if (payload.group && groupDraft) {
                setGroupDraft(payload.group);
            }
            return payload;
        } catch (err) {
            toast(err.response?.data?.error || err.message || "Unable to save.", "error");
            throw err;
        } finally {
            if (!quiet) setBusy(false);
        }
    }

    const byId = useMemo(() => new Map(items.map((row) => [row.id, row])), [items]);
    const kidsByParent = useMemo(() => childMap(items), [items]);
    const customersById = useMemo(() => new Map(customers.map((row) => [row.id, row])), [customers]);
    const customerLabel = (row) => {
        if (!row || row.scope !== "customer") return "General";
        return customersById.get(row.customerId)?.name || row.customerName || "Customer";
    };
    const liveGroups = useMemo(
        () =>
            groups
                .filter((row) => !row.archived)
                .slice()
                .sort((a, b) => String(a.sortOrder || "").localeCompare(String(b.sortOrder || ""))),
        [groups]
    );

    const plans = useMemo(() => items.filter((row) => !row.parentId), [items]);

    const visiblePlans = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return plans.filter((plan) => {
            if (customerIdParam && plan.customerId !== customerIdParam && filter === "customer") {
                if (!matchesFilter(plan, filter)) return false;
                if (plan.customerId !== customerIdParam) return false;
            } else if (!matchesFilter(plan, filter)) {
                return false;
            }
            if (!needle) return true;
            const customer = customersById.get(plan.customerId);
            return `${plan.title} ${plan.body} ${plan.nextAction} ${plan.customerName} ${plan.tags} ${customer?.company || ""} ${customer?.phone || ""} ${customer?.name || ""}`
                .toLowerCase()
                .includes(needle);
        });
    }, [plans, filter, query, customersById, customerIdParam]);

    const attentionCount = useMemo(
        () => plans.filter((row) => matchesFilter(row, "attention")).length,
        [plans]
    );

    const jobId = searchParams.get("job") || "";
    const focusId = searchParams.get("item") || jobId;
    const job = byId.get(jobId) || null;
    const focus = byId.get(focusId) || job;
    const quoteBuilt = useMemo(
        () => (job ? buildWixJobQuote(job, items, groups, pricingSettings, focus) : null),
        [job, items, groups, pricingSettings, focus]
    );
    const canBuildQuote = Boolean(quoteBuilt?.components?.length || quoteBuilt?.lines?.length);
    const quoteLocked = jobQuoteLocked(job);
    async function buildQuoteFromFocus() {
        if (!canBuildQuote || !job) return;
        try {
            await act("lock-quote", { id: job.id, keepDraft: false });
            navigate("/quotes", {
                state: { wixJobQuote: quoteBuilt, jobQuote: quoteBuilt },
            });
        } catch {
            /* toast already set */
        }
    }
    async function setJobLifecycle(state) {
        if (!job) return;
        try {
            await act("set-quote-state", { id: job.id, state, keepDraft: false });
            toast(state === "finalized" ? "Job marked finalized." : "Back to rough draft.");
        } catch {
            /* toast already set */
        }
    }
    async function reviseJobQuote() {
        if (!job) return;
        try {
            await act("revise-quote", { id: job.id, keepDraft: false });
            toast(`Unlocked as ${quoteRevLabel((job.quoteRev || 1) + 1)}.`);
        } catch {
            /* toast already set */
        }
    }
    function openLog(kind, itemId = "") {
        setLogKind(kind);
        setLogItemId(itemId);
        setLogOpen(true);
        setMoreAnchor(null);
        setAddAnchor(null);
    }
    async function saveLog(payload) {
        try {
            await act("save-action", { logged: payload, keepDraft: false });
            setLogOpen(false);
            toast(payload.kind === "time" ? "Hours logged." : "Material logged.");
        } catch {
            /* toast already set */
        }
    }
    function logTimeOn(item) {
        if (!item) return;
        if (!item.parentId) {
            navigate(`/job-actions?job=${item.id}`);
            return;
        }
        openLog("time", item.id);
    }
    const crumbs = useMemo(() => (focus ? crumbPath(focus, byId) : []), [focus, byId]);
    const parentItem = focus?.parentId ? byId.get(focus.parentId) : null;
    const directChildren = useMemo(
        () => (focus ? (kidsByParent.get(focus.id) || []).filter((row) => !row.archived) : []),
        [focus, kidsByParent]
    );
    const childrenByKind = useMemo(() => {
        const grouped = { component: [], labor: [], purchase: [] };
        for (const child of directChildren) {
            const kind = lineKindOf(child);
            if (grouped[kind]) grouped[kind].push(child);
            else grouped.component.push(child);
        }
        return grouped;
    }, [directChildren]);

    function selectJob(id) {
        setDraft(null);
        setEditOpen(false);
        setCheckTitle("");
        updateNav(id, id);
    }

    function selectNode(item) {
        if (!item) return;
        setDraft(null);
        setEditOpen(false);
        setCheckTitle("");
        updateNav(rootIdOf(item, byId), item.id);
    }

    function leaveJob() {
        setDraft(null);
        setEditOpen(false);
        setCheckTitle("");
        updateNav("", "");
    }

    function goUp() {
        if (parentItem) {
            selectNode(parentItem);
            return;
        }
        leaveJob();
    }

    function startPlan({ scope = "general", customer = null, parentId = "", lineKind = "", laborCategory = "" } = {}) {
        if (parentId && quoteLocked) {
            toast("This job is locked to a quote. Revise it before adding items.", "error");
            return;
        }
        const parent = byId.get(parentId);
        const linked = customer || customers.find((row) => row.id === parent?.customerId);
        const isGeneral = parent ? parent.scope === "general" : scope === "general";
        const nextKind = parentId ? lineKind || "component" : "job";
        const nextCategory = laborCategory || (nextKind === "labor" ? "design" : "");
        setDraft(
            blankItem({
                parentId,
                scope: isGeneral ? "general" : "customer",
                kind: isGeneral ? "idea" : "need",
                status: parent ? parent.status : isGeneral ? "idea" : "need",
                lineKind: nextKind,
                laborCategory: nextCategory,
                title: defaultTitleForKind(nextKind, nextCategory),
                groupKey: "",
                customerId: isGeneral ? "" : linked?.id || parent?.customerId || "",
                customerName: isGeneral ? "" : linked?.name || parent?.customerName || "",
                customerEmail: isGeneral ? "" : linked?.email || parent?.customerEmail || "",
            })
        );
        setEditOpen(true);
        setNewJobStep("");
        setCustomerQuery("");
        setGroupDraft(null);
    }

    function beginEdit(item) {
        setDraft({ ...blankItem(), ...item });
        setEditOpen(true);
        setCheckTitle("");
        setMoreAnchor(null);
        setAddAnchor(null);
    }

    function applyCustomer(nextId, current = draft) {
        const customer = customers.find((row) => row.id === nextId);
        if (!nextId) {
            return { ...current, scope: "general", customerId: "", customerName: "", customerEmail: "", kind: "idea" };
        }
        return {
            ...current,
            scope: "customer",
            kind: current.kind === "idea" ? "need" : current.kind,
            customerId: nextId,
            customerName: customer?.name || "",
            customerEmail: customer?.email || "",
        };
    }

    async function saveDraft() {
        if (!draft?.title?.trim()) {
            toast("Give this a title.", "error");
            return;
        }
        try {
            const payload = await act("save-item", {
                item: draft,
                keepDraft: false,
                navigateToItem: !draft.id && !draft.parentId,
            });
            setDraft(null);
            setEditOpen(false);
            toast(draft.id ? "Saved." : "Added.");
            return payload;
        } catch {
            /* toast already set */
        }
    }

    function startChild(lineKind) {
        if (!focus) return;
        setAddAnchor(null);
        startPlan({ parentId: focus.id, lineKind });
    }

    async function addSkeleton(skeleton) {
        if (!focus) return;
        try {
            await act("add-branches", { parentId: focus.id, branches: skeleton.branches, keepDraft: false });
            setSkeletonAnchor(null);
            setAddAnchor(null);
            toast(`Added ${skeleton.label}.`);
        } catch {
            /* toast already set */
        }
    }

    async function saveGroup() {
        if (!groupDraft?.label?.trim()) {
            toast("Give the group a name.", "error");
            return;
        }
        try {
            const payload = await act("save-group", { group: groupDraft, keepDraft: false });
            if (payload.group) setGroupDraft(payload.group);
            toast(groupDraft.id ? "Group saved." : "Group created.");
        } catch {
            /* toast already set */
        }
    }

    function openGroupsManager(seed = null) {
        setGroupDraft(seed || blankGroup());
        setGroupsOpen(true);
        setMoreAnchor(null);
    }

    async function runConfirm() {
        if (!confirm) return;
        try {
            if (confirm.kind === "item") {
                const doomed = byId.get(confirm.id);
                await act("delete-item", { id: confirm.id });
                if (draft?.id === confirm.id) {
                    setDraft(null);
                    setEditOpen(false);
                }
                if (focusId === confirm.id) {
                    if (doomed?.parentId) updateNav(jobId, doomed.parentId);
                    else leaveJob();
                } else if (jobId === confirm.id) {
                    leaveJob();
                }
                toast("Deleted.");
            } else if (confirm.kind === "group") {
                await act("delete-group", { id: confirm.id });
                setGroupDraft(blankGroup());
                toast("Group deleted.");
            }
            setConfirm(null);
        } catch {
            setConfirm(null);
        }
    }

    async function addCheck(itemId) {
        const value = checkTitle.trim();
        if (!itemId || !value) return;
        try {
            await act("save-check", { itemId, title: value, keepDraft: false, quiet: true });
            setCheckTitle("");
        } catch {
            /* toast already set */
        }
    }

    async function toggleCheck(check, done) {
        try {
            await act("toggle-check", { id: check.id, done, keepDraft: false, quiet: true });
        } catch {
            /* toast already set */
        }
    }

    async function removeCheck(check) {
        try {
            await act("delete-check", { id: check.id, keepDraft: false, quiet: true });
        } catch {
            /* toast already set */
        }
    }

    async function importWix() {
        if (!isAdmin) return;
        setMoreAnchor(null);
        try {
            const payload = await act("import-wix", { keepDraft: false });
            const imported = payload.import;
            toast(
                imported
                    ? `Imported ${imported.itemsInserted || 0} items, ${imported.groupsInserted || 0} groups, ${imported.checksInserted || 0} checks. Customer links: ${imported.customerLinksResolved || 0} matched, ${imported.customerLinksUpdated || 0} updated${imported.customerLinksUnresolved ? `, ${imported.customerLinksUnresolved} need review` : ""}.`
                    : "Import from Wix complete."
            );
        } catch {
            /* toast already set */
        }
    }

    async function seedGroups() {
        try {
            await act("seed-groups", { keepDraft: false });
            toast("Default groups seeded.");
        } catch {
            /* toast already set */
        }
    }

    const filteredCustomers = useMemo(() => {
        const needle = customerQuery.trim().toLowerCase();
        if (!needle) return customers;
        return customers.filter((customer) =>
            `${customer.name} ${customer.company} ${customer.email}`.toLowerCase().includes(needle)
        );
    }, [customers, customerQuery]);

    const draftKind = draft ? lineKindOf(draft) : null;
    const draftLive = draft?.id ? byId.get(draft.id) : null;
    const draftGroup = draft ? liveGroups.find((g) => g.key === draft.groupKey) : null;
    const draftQuote = draft
        ? itemQuotePreview({ ...draft, groupLabel: draftGroup?.label || draft.title || "" }, childQuoteOf(draftLive))
        : null;
    const draftJob = draft?.parentId ? byId.get(rootIdOf(draft, byId)) : draft;
    const draftQuoteLocked = jobQuoteLocked(draftJob || (draft?.parentId ? job : null));
    const draftParentHidden = Boolean((() => {
        if (!draft?.parentId) return false;
        let parent = byId.get(draft.parentId);
        const seen = new Set();
        while (parent && !seen.has(parent.id)) {
            seen.add(parent.id);
            if (parent.hideFromCustomer) return true;
            parent = byId.get(parent.parentId);
        }
        return false;
    })());

    function setLineKind(nextKind) {
        if (!draft) return;
        const keepTitle = draft.title && draft.title !== defaultTitleForKind(draftKind, draft.laborCategory);
        const nextCategory = nextKind === "labor" ? draft.laborCategory || "design" : "";
        setDraft({
            ...draft,
            lineKind: nextKind,
            laborCategory: nextCategory,
            title: keepTitle ? draft.title : defaultTitleForKind(nextKind, nextCategory) || draft.title,
        });
    }

    const closeEdit = () => {
        if (!busy) setEditOpen(false);
    };

    const editDialog = (
        <Dialog
            open={editOpen && Boolean(draft)}
            onClose={closeEdit}
            fullWidth
            maxWidth="md"
            fullScreen={isMobile}
            scroll="paper"
        >
            {isMobile ? (
                <AppBar sx={{ position: "relative" }} color="transparent" elevation={0}>
                    <Toolbar sx={{ gap: 1, borderBottom: 1, borderColor: "divider" }}>
                        <IconButton edge="start" onClick={closeEdit} disabled={busy} aria-label="Close">
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ flex: 1, fontWeight: 800 }} noWrap>
                            {draft?.id
                                ? `Edit ${lineKindLabel(draftKind || "job").toLowerCase()}`
                                : `New ${lineKindLabel(draftKind || "job").toLowerCase()}`}
                        </Typography>
                        <Button variant="contained" disabled={busy} onClick={saveDraft}>
                            Save
                        </Button>
                    </Toolbar>
                </AppBar>
            ) : (
                <DialogTitle>
                    {draft?.id
                        ? `Edit ${lineKindLabel(draftKind || "job").toLowerCase()}`
                        : `New ${lineKindLabel(draftKind || "job").toLowerCase()}`}
                </DialogTitle>
            )}
            <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
                {draft ? (
                    <Stack spacing={2} sx={{ pt: 0.5, pb: isMobile ? 10 : 0 }}>
                        {draftQuoteLocked ? (
                            <Alert severity="warning" variant="outlined">
                                Quote {quoteRevLabel(draftJob?.quoteRev)} is locked. You can update work status, next
                                action, and due date. Revise the job to change scope or pricing.
                            </Alert>
                        ) : null}

                        {!draft.parentId ? (
                            <FormControl fullWidth size="small">
                                <InputLabel>Customer link</InputLabel>
                                <Select
                                    label="Customer link"
                                    value={draft.customerId || ""}
                                    disabled={draftQuoteLocked}
                                    onChange={(e) => setDraft(applyCustomer(e.target.value))}
                                >
                                    <MenuItem value="">General (no customer)</MenuItem>
                                    {customers.map((customer) => (
                                        <MenuItem key={customer.id} value={customer.id}>
                                            {customer.name}
                                            {customer.company ? ` · ${customer.company}` : ""}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Nested under parent · stays with{" "}
                                {byId.get(draft.parentId)?.scope === "customer"
                                    ? byId.get(draft.parentId)?.customerName || "customer"
                                    : "general"}
                            </Typography>
                        )}

                        {draft.parentId ? (
                            <FormControl fullWidth size="small">
                                <InputLabel>Line type</InputLabel>
                                <Select
                                    label="Line type"
                                    value={draftKind === "job" ? "component" : draftKind}
                                    disabled={draftQuoteLocked}
                                    onChange={(e) => setLineKind(e.target.value)}
                                >
                                    {PLANNER_CHILD_LINE_KINDS.map((row) => (
                                        <MenuItem key={row.id} value={row.id}>
                                            {row.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : null}

                        <TextField
                            label="Title"
                            size="small"
                            fullWidth
                            autoFocus={!isMobile}
                            value={draft.title}
                            disabled={draftQuoteLocked}
                            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                            placeholder={
                                draftKind === "labor"
                                    ? "Design time, install, programming…"
                                    : draftKind === "purchase"
                                        ? "UR20, Schunk gripper, fence kit…"
                                        : draft.parentId
                                            ? "Sanding station, gripper, pedestal…"
                                            : "What is the job?"
                            }
                        />

                        <Stack spacing={0.5}>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1.5}
                                useFlexGap
                                flexWrap="wrap"
                                alignItems={{ sm: "flex-start" }}
                            >
                                {draftKind !== "labor" ? (
                                    <FormControl size="small" sx={{ flex: "1 1 200px", minWidth: { xs: "100%", sm: 180 } }}>
                                        <InputLabel>{draftKind === "purchase" ? "Buy group" : "Group"}</InputLabel>
                                        <Select
                                            label={draftKind === "purchase" ? "Buy group" : "Group"}
                                            value={draft.groupKey || ""}
                                            disabled={draftQuoteLocked}
                                            onChange={(e) => setDraft({ ...draft, groupKey: e.target.value })}
                                        >
                                            <MenuItem value="">Ungrouped</MenuItem>
                                            {liveGroups.map((group) => (
                                                <MenuItem key={group.key} value={group.key}>
                                                    {group.icon ? `${group.icon} ` : ""}
                                                    {group.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <FormControl size="small" sx={{ flex: "1 1 200px", minWidth: { xs: "100%", sm: 180 } }}>
                                        <InputLabel>Labor type</InputLabel>
                                        <Select
                                            label="Labor type"
                                            value={draft.laborCategory || "design"}
                                            disabled={draftQuoteLocked}
                                            onChange={(e) => {
                                                const laborCategory = e.target.value;
                                                const keepTitle =
                                                    draft.title &&
                                                    draft.title !== defaultTitleForKind("labor", draft.laborCategory);
                                                setDraft({
                                                    ...draft,
                                                    laborCategory,
                                                    title: keepTitle
                                                        ? draft.title
                                                        : defaultTitleForKind("labor", laborCategory),
                                                });
                                            }}
                                        >
                                            {LABOR_CATEGORIES.map((row) => (
                                                <MenuItem key={row.id} value={row.id}>
                                                    {row.label} · ${row.hourlyRate}/hr
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                                <FormControl size="small" sx={{ flex: "1 1 140px", minWidth: { xs: "100%", sm: 140 } }}>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        label="Status"
                                        value={draft.status}
                                        onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                                    >
                                        {PLANNER_STATUSES.map((row) => (
                                            <MenuItem key={row.id} value={row.id}>
                                                {row.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ flex: "1 1 140px", minWidth: { xs: "100%", sm: 140 } }}>
                                    <InputLabel>Priority</InputLabel>
                                    <Select
                                        label="Priority"
                                        value={draft.priority}
                                        onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
                                    >
                                        {PLANNER_PRIORITIES.map((row) => (
                                            <MenuItem key={row.id} value={row.id}>
                                                {row.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                            {draftKind !== "labor" ? (
                                <Button
                                    size="small"
                                    onClick={() => openGroupsManager()}
                                    sx={{ alignSelf: "flex-start" }}
                                >
                                    Add or edit groups…
                                </Button>
                            ) : null}
                        </Stack>

                        {(draftKind === "job" || draftKind === "component") && (
                            <TextField
                                label="Lead time"
                                size="small"
                                fullWidth
                                value={draft.leadTime || ""}
                                disabled={draftQuoteLocked}
                                onChange={(e) => setDraft({ ...draft, leadTime: e.target.value })}
                                placeholder="2 days, 4 weeks, TBD"
                            />
                        )}

                        <Divider />
                        <Typography variant="overline" color="text.secondary">
                            Price metrics
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                            <TextField
                                label="Contingency %"
                                size="small"
                                fullWidth
                                type="number"
                                inputProps={{ min: 0, step: 0.5 }}
                                value={draft.contingencyPct || ""}
                                disabled={draftQuoteLocked}
                                onChange={(e) => setDraft({ ...draft, contingencyPct: e.target.value })}
                                helperText="Optional buffer on this item and descendants"
                            />
                            <TextField
                                label="Risk-loaded total"
                                size="small"
                                fullWidth
                                value={formatMoney(draftQuote?.loadedTotal) || "—"}
                                InputProps={{ readOnly: true }}
                                helperText="Built-up total plus contingency"
                            />
                        </Stack>

                        {draftKind === "labor" ? (
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                                <TextField
                                    label="Hours"
                                    size="small"
                                    fullWidth
                                    value={draft.laborEstimate || ""}
                                    disabled={draftQuoteLocked}
                                    onChange={(e) => setDraft({ ...draft, laborEstimate: e.target.value })}
                                    placeholder="16"
                                />
                                <TextField
                                    label="Labor cost override"
                                    size="small"
                                    fullWidth
                                    value={draft.costEstimate || ""}
                                    disabled={draftQuoteLocked}
                                    onChange={(e) => setDraft({ ...draft, costEstimate: e.target.value })}
                                    placeholder={
                                        draftQuote?.laborHours && !draft.costEstimate
                                            ? formatMoney(draftQuote.cost) || "Auto from hours × rate"
                                            : "Override auto cost"
                                    }
                                    helperText={
                                        draftQuote?.laborHours && !draft.costEstimate ? "Hours × rate" : undefined
                                    }
                                />
                            </Stack>
                        ) : null}

                        {draftKind === "purchase" ? (
                            <Stack spacing={1.5}>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                                    <TextField
                                        label="Qty"
                                        size="small"
                                        fullWidth
                                        value={draft.qty || ""}
                                        disabled={draftQuoteLocked}
                                        onChange={(e) => setDraft({ ...draft, qty: e.target.value })}
                                        placeholder="1"
                                    />
                                    <TextField
                                        label="Unit cost"
                                        size="small"
                                        fullWidth
                                        value={draft.unitCost || ""}
                                        disabled={draftQuoteLocked}
                                        onChange={(e) => setDraft({ ...draft, unitCost: e.target.value })}
                                        placeholder="2400"
                                    />
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Treatment</InputLabel>
                                        <Select
                                            label="Treatment"
                                            value={draft.purchaseTreatment || "pbr"}
                                            disabled={draftQuoteLocked}
                                            onChange={(e) =>
                                                setDraft({ ...draft, purchaseTreatment: e.target.value })
                                            }
                                        >
                                            {PURCHASE_TREATMENTS.map((row) => (
                                                <MenuItem key={row.id} value={row.id}>
                                                    {row.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                                <TextField
                                    label="Line total"
                                    size="small"
                                    fullWidth
                                    value={
                                        draft.unitCost
                                            ? formatMoney(draftQuote?.purchases) || ""
                                            : draft.purchaseEstimate || ""
                                    }
                                    onChange={(e) => setDraft({ ...draft, purchaseEstimate: e.target.value })}
                                    InputProps={{ readOnly: Boolean(draft.unitCost) || draftQuoteLocked }}
                                    helperText={draft.unitCost ? "Qty × unit" : undefined}
                                    placeholder="3500"
                                />
                                <TextField
                                    label="Purchase notes"
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={draft.purchaseNotes || ""}
                                    disabled={draftQuoteLocked}
                                    onChange={(e) => setDraft({ ...draft, purchaseNotes: e.target.value })}
                                    placeholder="Vendor, part number, lead"
                                />
                            </Stack>
                        ) : null}

                        {draft.parentId ? (
                            <FormControlLabel
                                sx={{ alignItems: "flex-start", ml: 0, mr: 0 }}
                                control={
                                    <Switch
                                        checked={Boolean(draft.hideFromCustomer) || draftParentHidden}
                                        disabled={draftParentHidden || draftQuoteLocked}
                                        onChange={(e) => setDraft({ ...draft, hideFromCustomer: e.target.checked })}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            Block from customer quote
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {draftParentHidden
                                                ? "A parent item is already blocked, so this stays on the internal quote only."
                                                : "Shows on the internal quote only. Excluded from customer packages and customer price totals."}
                                        </Typography>
                                    </Box>
                                }
                            />
                        ) : null}

                        {(draftKind === "job" || draftKind === "component") && (
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                                <TextField
                                    label="Estimate / budget target"
                                    size="small"
                                    fullWidth
                                    value={draft.budgetTarget || ""}
                                    disabled={draftQuoteLocked}
                                    onChange={(e) => setDraft({ ...draft, budgetTarget: e.target.value })}
                                    placeholder={draftKind === "job" ? "$180,000 quoted" : "$85,000 for this station"}
                                    helperText="Target for this layer"
                                />
                                <TextField
                                    label="Built-up so far"
                                    size="small"
                                    fullWidth
                                    value={formatMoney(draftQuote?.rolledTotal) || "—"}
                                    InputProps={{ readOnly: true }}
                                />
                                <TextField
                                    label="vs estimate"
                                    size="small"
                                    fullWidth
                                    value={
                                        draftQuote?.variance
                                            ? `${formatMoney(Math.abs(draftQuote.variance.amount))} ${draftQuote.variance.over
                                                ? "over"
                                                : draftQuote.variance.under
                                                    ? "left"
                                                    : "even"
                                            }`
                                            : "—"
                                    }
                                    InputProps={{ readOnly: true }}
                                />
                            </Stack>
                        )}

                        <Divider />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Kind</InputLabel>
                                <Select
                                    label="Kind"
                                    value={draft.kind}
                                    disabled={draftQuoteLocked}
                                    onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
                                >
                                    {PLANNER_KINDS.map((row) => (
                                        <MenuItem key={row.id} value={row.id}>
                                            {row.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Next action"
                                size="small"
                                fullWidth
                                value={draft.nextAction || ""}
                                onChange={(e) => setDraft({ ...draft, nextAction: e.target.value })}
                                placeholder="Call, quote, send layout"
                            />
                            <TextField
                                label="Due date"
                                size="small"
                                fullWidth
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={draft.dueDate || ""}
                                onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
                            />
                        </Stack>

                        <TextField
                            label="Body / notes"
                            size="small"
                            fullWidth
                            multiline
                            minRows={3}
                            value={draft.body || ""}
                            disabled={draftQuoteLocked}
                            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                            placeholder="Scope, constraints, shop-floor notes"
                        />

                        <TextField
                            label="Tags"
                            size="small"
                            fullWidth
                            value={draft.tags || ""}
                            onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
                            placeholder="fanuc, dual-grip, quote-sent"
                        />

                        <Stack direction="row" spacing={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={Boolean(draft.pinned)}
                                        onChange={(e) => setDraft({ ...draft, pinned: e.target.checked })}
                                    />
                                }
                                label="Pin"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={Boolean(draft.archived)}
                                        disabled={draftQuoteLocked}
                                        onChange={(e) => setDraft({ ...draft, archived: e.target.checked })}
                                    />
                                }
                                label="Archive"
                            />
                        </Stack>

                        {draft.id ? (
                            <Box>
                                <Typography variant="overline" color="text.secondary">
                                    Checklist
                                </Typography>
                                <List dense disablePadding>
                                    {(draftLive?.checks || draft.checks || []).map((check) => (
                                        <ListItem key={check.id} disablePadding secondaryAction={
                                            <IconButton edge="end" size="small" disabled={draftQuoteLocked} onClick={() => removeCheck(check)} aria-label="Delete check">
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        }>
                                            <Checkbox
                                                edge="start"
                                                checked={Boolean(check.done)}
                                                onChange={(e) => toggleCheck(check, e.target.checked)}
                                            />
                                            <ListItemText
                                                primary={check.title}
                                                sx={{
                                                    textDecoration: check.done ? "line-through" : "none",
                                                    color: check.done ? "text.secondary" : "text.primary",
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={checkTitle}
                                        onChange={(e) => setCheckTitle(e.target.value)}
                                        placeholder="Add a step…"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                addCheck(draft.id);
                                            }
                                        }}
                                    />
                                    <Button
                                        variant="outlined"
                                        disabled={!checkTitle.trim() || busy || draftQuoteLocked}
                                        onClick={() => addCheck(draft.id)}
                                    >
                                        Add
                                    </Button>
                                </Stack>
                            </Box>
                        ) : null}
                    </Stack>
                ) : null}
            </DialogContent>
            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    justifyContent: "space-between",
                    display: isMobile ? "none" : "flex",
                }}
            >
                <Box>
                    {draft?.id ? (
                        <Button
                            color="error"
                            disabled={busy || draftQuoteLocked}
                            onClick={() =>
                                setConfirm({
                                    kind: "item",
                                    id: draft.id,
                                    title: draft.parentId
                                        ? `Delete this ${lineKindLabel(lineKindOf(draft)).toLowerCase()}?`
                                        : "Delete this job?",
                                    body: "Everything nested under it is deleted too. This cannot be undone.",
                                })
                            }
                        >
                            Delete
                        </Button>
                    ) : null}
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button disabled={busy} onClick={closeEdit}>
                        Cancel
                    </Button>
                    <Button variant="contained" disabled={busy} onClick={saveDraft}>
                        Save
                    </Button>
                </Stack>
            </DialogActions>
            {isMobile && draft?.id ? (
                <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider" }}>
                    <Button
                        color="error"
                        fullWidth
                        disabled={busy || draftQuoteLocked}
                        onClick={() =>
                            setConfirm({
                                kind: "item",
                                id: draft.id,
                                title: draft.parentId
                                    ? `Delete this ${lineKindLabel(lineKindOf(draft)).toLowerCase()}?`
                                    : "Delete this job?",
                                body: "Everything nested under it is deleted too. This cannot be undone.",
                            })
                        }
                    >
                        Delete
                    </Button>
                </Box>
            ) : null}
        </Dialog>
    );

    const moreMenu = (
        <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
            <MenuItem
                onClick={() => {
                    setMoreAnchor(null);
                    setNewJobStep("scope");
                }}
                disabled={busy}
            >
                <AddIcon fontSize="small" sx={{ mr: 1 }} /> New job
            </MenuItem>
            <MenuItem
                onClick={() => {
                    setMoreAnchor(null);
                    load();
                }}
                disabled={loading || busy}
            >
                <RefreshIcon fontSize="small" sx={{ mr: 1 }} /> Refresh
            </MenuItem>
            <MenuItem onClick={() => openGroupsManager()} disabled={busy}>
                <SettingsIcon fontSize="small" sx={{ mr: 1 }} /> Manage groups
            </MenuItem>
            {isAdmin ? (
                <MenuItem onClick={importWix} disabled={busy}>
                    <CloudDownloadIcon fontSize="small" sx={{ mr: 1 }} /> Import from Wix
                </MenuItem>
            ) : null}
            {job ? (
                [
                    <MenuItem key="log-time" onClick={() => openLog("time", lineKindOf(focus) === "labor" ? focus.id : "")}>
                        <MoreTimeIcon fontSize="small" sx={{ mr: 1 }} /> Log time
                    </MenuItem>,
                    <MenuItem key="log-mat" onClick={() => openLog("material")}>
                        <Inventory2Icon fontSize="small" sx={{ mr: 1 }} /> Extra material
                    </MenuItem>,
                    <MenuItem
                        key="job-actions"
                        onClick={() => {
                            setMoreAnchor(null);
                            navigate(`/job-actions?job=${job.id}`);
                        }}
                    >
                        Job actions
                    </MenuItem>,
                ]
            ) : null}
        </Menu>
    );

    const addMenu = (
        <Menu anchorEl={addAnchor} open={Boolean(addAnchor)} onClose={() => setAddAnchor(null)}>
            {PLANNER_CHILD_LINE_KINDS.map((row) => (
                <MenuItem key={row.id} disabled={busy || !focus || quoteLocked} onClick={() => startChild(row.id)}>
                    Add {row.label.toLowerCase()}
                </MenuItem>
            ))}
            <Divider />
            {PLANNER_SKELETONS.map((skeleton) => (
                <MenuItem key={skeleton.id} disabled={busy || !focus || quoteLocked} onClick={() => addSkeleton(skeleton)}>
                    {skeleton.label}
                </MenuItem>
            ))}
        </Menu>
    );

    const headerActions = (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {isMobile ? (
                <>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        disabled={busy}
                        onClick={() => setNewJobStep("scope")}
                    >
                        New job
                    </Button>
                    <IconButton onClick={(e) => setMoreAnchor(e.currentTarget)} aria-label="More planner actions">
                        <MoreVertIcon />
                    </IconButton>
                </>
            ) : (
                <>
                    <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading || busy}>
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        disabled={busy}
                        onClick={() => setNewJobStep("scope")}
                    >
                        New job
                    </Button>
                    <Button startIcon={<SettingsIcon />} disabled={busy} onClick={() => openGroupsManager()}>
                        Manage groups
                    </Button>
                    {isAdmin ? (
                        <Button startIcon={<CloudDownloadIcon />} disabled={busy} onClick={importWix}>
                            Import from Wix
                        </Button>
                    ) : null}
                </>
            )}
        </Stack>
    );

    function renderChildSections() {
        if (!focus) return null;
        return (
            <Stack spacing={2.5}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    spacing={1}
                >
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            Child items
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {directChildren.length
                                ? `${directChildren.length} under this ${lineKindLabel(lineKindOf(focus)).toLowerCase()}`
                                : "Nothing nested here yet. Add a component, labor, or purchase."}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {PLANNER_CHILD_LINE_KINDS.map((row) => (
                            <Button
                                key={row.id}
                                size="small"
                                variant="outlined"
                                disabled={busy || quoteLocked}
                                startIcon={<AddIcon />}
                                onClick={() => startPlan({ parentId: focus.id, lineKind: row.id })}
                                sx={(theme) => ({
                                    borderColor: alpha(plannerKindAccent(theme, row.id), 0.55),
                                    color: plannerKindAccent(theme, row.id),
                                })}
                            >
                                {row.label}
                            </Button>
                        ))}
                        <Button
                            size="small"
                            variant="contained"
                            disabled={busy || quoteLocked}
                            onClick={(e) => setSkeletonAnchor(e.currentTarget)}
                        >
                            Add skeleton
                        </Button>
                    </Stack>
                </Stack>
                <Menu
                    anchorEl={skeletonAnchor}
                    open={Boolean(skeletonAnchor)}
                    onClose={() => setSkeletonAnchor(null)}
                >
                    {PLANNER_SKELETONS.map((skeleton) => (
                        <MenuItem key={skeleton.id} onClick={() => addSkeleton(skeleton)} disabled={busy || quoteLocked}>
                            <ListItemText primary={skeleton.label} secondary={skeleton.blurb} />
                        </MenuItem>
                    ))}
                </Menu>

                {CHILD_SECTIONS.map((section) => {
                    const rows = childrenByKind[section.id] || [];
                    if (!rows.length) return null;
                    const filledSection = section.id !== "component";
                    return (
                        <Box
                            key={section.id}
                            sx={(theme) => ({
                                p: filledSection ? { xs: 1.25, sm: 1.75 } : 0,
                                ...(filledSection ? kindSurfaceSx(theme, section.id) : {}),
                                borderRadius: 1,
                            })}
                        >
                            <Typography
                                variant="overline"
                                sx={(theme) => ({
                                    display: "block",
                                    mb: 1.25,
                                    fontWeight: 800,
                                    letterSpacing: "0.12em",
                                    color: plannerKindAccent(theme, section.id),
                                })}
                            >
                                {section.label} · {rows.length}
                            </Typography>
                            <Stack spacing={1.25}>
                                {rows.map((child) => (
                                    <ItemCard
                                        key={child.id}
                                        item={child}
                                        dense
                                        childCount={(kidsByParent.get(child.id) || []).filter((row) => !row.archived).length}
                                        onOpen={selectNode}
                                        onEdit={beginEdit}
                                        onLogTime={logTimeOn}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>
        );
    }

    const missingJob = Boolean(jobId) && !loading && !job;

    return (
        <Box sx={{ pb: isMobile && focus ? 10 : 0 }}>
            {!(isMobile && job) ? (
                <PageHeader
                    badge="Operations"
                    title="Planner"
                    subtitle={
                        isMobile
                            ? "Open a job to work its child items."
                            : "Open a job, then work one layer at a time. Edit any item from the pencil on its card."
                    }
                    action={headerActions}
                />
            ) : null}

            {!job ? (
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                    <Box sx={{ overflowX: "auto", mx: { xs: -0.5, sm: 0 } }}>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={filter}
                            onChange={(_e, next) => {
                                if (next) setFilter(next);
                            }}
                            sx={{ flexWrap: { xs: "nowrap", sm: "wrap" } }}
                        >
                            {FILTERS.map((row) => (
                                <ToggleButton key={row.id} value={row.id} sx={{ whiteSpace: "nowrap" }}>
                                    {row.label}
                                    {row.id === "attention" && attentionCount ? ` (${attentionCount})` : ""}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                    <TextField
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search jobs, contacts, next action…"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: { md: 420 } }}
                    />
                </Stack>
            ) : null}

            {loading ? (
                <Stack alignItems="center" py={6}>
                    <CircularProgress />
                </Stack>
            ) : missingJob ? (
                <Paper sx={{ p: 3 }}>
                    <Typography sx={{ mb: 1.5 }}>That job is not on this list.</Typography>
                    <Button variant="contained" onClick={leaveJob}>
                        Back to jobs
                    </Button>
                </Paper>
            ) : !job ? (
                visiblePlans.length ? (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                            gap: 1.5,
                        }}
                    >
                        {visiblePlans.map((row) => (
                            <Box key={row.id}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, px: 0.5 }}>
                                    {customerLabel(row)}
                                    {row.pinned ? " · pinned" : ""}
                                </Typography>
                                <ItemCard
                                    item={row}
                                    childCount={(kidsByParent.get(row.id) || []).filter((child) => !child.archived).length}
                                    onOpen={(item) => selectJob(item.id)}
                                    onEdit={beginEdit}
                                    onLogTime={logTimeOn}
                                />
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Paper sx={{ p: 3 }}>
                        <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                            No jobs match this filter.
                        </Typography>
                        <Button variant="contained" onClick={() => setNewJobStep("scope")}>
                            New job
                        </Button>
                    </Paper>
                )
            ) : (
                <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton onClick={goUp} aria-label="Back" sx={{ width: 44, height: 44 }}>
                            <ArrowBackIcon />
                        </IconButton>
                        {isMobile ? (
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                                {parentItem ? parentItem.title : "All jobs"}
                            </Typography>
                        ) : (
                            <Breadcrumbs sx={{ flex: 1, minWidth: 0 }}>
                                <Link component="button" underline="hover" color="inherit" onClick={leaveJob}>
                                    Jobs
                                </Link>
                                {crumbs.slice(0, -1).map((crumb) => (
                                    <Link
                                        key={crumb.id}
                                        component="button"
                                        underline="hover"
                                        color="inherit"
                                        onClick={() => selectNode(crumb)}
                                    >
                                        {crumb.title}
                                    </Link>
                                ))}
                                <Typography color="text.primary" sx={{ fontWeight: 700 }}>
                                    {focus?.title}
                                </Typography>
                            </Breadcrumbs>
                        )}
                        {isMobile ? (
                            <IconButton onClick={(e) => setMoreAnchor(e.currentTarget)} aria-label="More planner actions">
                                <MoreVertIcon />
                            </IconButton>
                        ) : null}
                    </Stack>

                    <Paper
                        sx={(t) => ({
                            ...panelCardSx(t),
                            ...kindSurfaceSx(t, lineKindOf(focus)),
                            p: { xs: 1.75, sm: 2.5 },
                        })}
                    >
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ sm: "flex-start" }}
                            spacing={1.5}
                        >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="overline" color="text.secondary">
                                    {customerLabel(job)}
                                    {focus?.id !== job.id ? ` · ${lineKindLabel(lineKindOf(focus))}` : ""}
                                </Typography>
                                <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                    {focus?.pinned ? "★ " : ""}
                                    {focus?.title}
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                    <LineKindChip kind={lineKindOf(focus)} />
                                    <StatusChip status={focus.status} />
                                    {focus.priority !== "normal" ? (
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            color={focus.priority === "urgent" ? "error" : "warning"}
                                            label={priorityLabel(focus.priority)}
                                        />
                                    ) : null}
                                    {focus.hideFromCustomer ? (
                                        <Chip size="small" color="warning" variant="outlined" label="Internal only" />
                                    ) : null}
                                    {job ? (
                                        <Chip
                                            size="small"
                                            color={quoteStateColor(job.quoteState)}
                                            label={`${quoteStateLabel(job.quoteState)} · ${quoteRevLabel(job.quoteRev)}`}
                                        />
                                    ) : null}
                                    {job?.quoteDirty ? <Chip size="small" color="warning" label="Quote stale" /> : null}
                                    {job?.actualHours ? (
                                        <Chip
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                            label={`${formatHours(job.actualHours)} logged`}
                                        />
                                    ) : null}
                                    <FactChip label="Est" value={formatMoney(focus.quoteEstimate)} />
                                    <FactChip label="Built" value={formatMoney(focus.quoteRollupTotal)} />
                                    {focus.quoteLoadedTotal != null && focus.quoteContingencyPct ? (
                                        <FactChip
                                            label="Loaded"
                                            value={`${formatMoney(focus.quoteLoadedTotal)} (+${focus.quoteContingencyPct}%)`}
                                        />
                                    ) : null}
                                    <FactChip label="Labor" value={formatHours(focus.quoteRollupLaborHours)} />
                                </Stack>
                                {focus.nextAction ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Next: {focus.nextAction}
                                    </Typography>
                                ) : null}
                                {focus.body ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                                        {focus.body}
                                    </Typography>
                                ) : null}
                                {focus?.id === job?.id ? (
                                    <JobLifecycleBar
                                        job={job}
                                        busy={busy}
                                        onSetState={setJobLifecycle}
                                        onRevise={reviseJobQuote}
                                    />
                                ) : quoteLocked || job?.quoteDirty ? (
                                    <JobLifecycleBar
                                        job={job}
                                        busy={busy}
                                        compact
                                        onSetState={setJobLifecycle}
                                        onRevise={reviseJobQuote}
                                    />
                                ) : null}
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ display: { xs: "none", md: "flex" } }}>
                                <Button variant="contained" startIcon={<EditIcon />} onClick={() => beginEdit(focus)}>
                                    Edit
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<RequestQuoteIcon />}
                                    onClick={buildQuoteFromFocus}
                                    disabled={!canBuildQuote}
                                >
                                    Build quote
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<MoreTimeIcon />}
                                    onClick={() =>
                                        openLog("time", lineKindOf(focus) === "labor" ? focus.id : "")
                                    }
                                >
                                    Log time
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<Inventory2Icon />}
                                    onClick={() => openLog("material", lineKindOf(focus) === "purchase" ? focus.id : "")}
                                >
                                    Extra material
                                </Button>
                                <Button onClick={() => navigate(`/job-actions?job=${job.id}`)}>Job actions</Button>
                                <Button
                                    color="error"
                                    disabled={quoteLocked}
                                    onClick={() =>
                                        setConfirm({
                                            kind: "item",
                                            id: focus.id,
                                            title: focus.parentId
                                                ? `Delete this ${lineKindLabel(lineKindOf(focus)).toLowerCase()}?`
                                                : "Delete this job?",
                                            body: "Everything nested under it is deleted too. This cannot be undone.",
                                        })
                                    }
                                >
                                    Delete
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>

                    {renderChildSections()}

                    <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
                        <Typography variant="overline" color="text.secondary">
                            Checklist
                        </Typography>
                        <Box sx={{ mb: 0.5 }}>
                            <Completion done={focus.checkDone} total={focus.checkTotal} empty="No checklist" />
                        </Box>
                        <List dense disablePadding>
                            {(focus.checks || []).map((check) => (
                                <ListItem
                                    key={check.id}
                                    disablePadding
                                    secondaryAction={
                                        <IconButton
                                            edge="end"
                                            size="small"
                                            disabled={quoteLocked}
                                            onClick={() => removeCheck(check)}
                                            aria-label="Delete check"
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    }
                                >
                                    <Checkbox
                                        edge="start"
                                        checked={Boolean(check.done)}
                                        onChange={(e) => toggleCheck(check, e.target.checked)}
                                    />
                                    <ListItemText
                                        primary={check.title}
                                        sx={{
                                            textDecoration: check.done ? "line-through" : "none",
                                            color: check.done ? "text.secondary" : "text.primary",
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <TextField
                                size="small"
                                fullWidth
                                value={checkTitle}
                                onChange={(e) => setCheckTitle(e.target.value)}
                                placeholder="Add a step…"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addCheck(focus.id);
                                    }
                                }}
                            />
                            <Button
                                variant="outlined"
                                disabled={!checkTitle.trim() || busy || quoteLocked}
                                onClick={() => addCheck(focus.id)}
                            >
                                Add
                            </Button>
                        </Stack>
                    </Paper>

                    <Accordion
                        defaultExpanded={!isMobile}
                        disableGutters
                        sx={(t) => ({
                            ...panelCardSx(t),
                            "&:before": { display: "none" },
                        })}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontWeight: 800 }}>Quote & details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <WixJobQuotePanel
                                job={job}
                                focus={focus}
                                items={items}
                                groups={groups}
                                pricingSettings={pricingSettings}
                                onEditItem={(node) => {
                                    const live = byId.get(node?.id) || node;
                                    if (!live?.id) return;
                                    updateNav(rootIdOf(live, byId), live.id);
                                    beginEdit(live);
                                }}
                            />
                        </AccordionDetails>
                    </Accordion>
                </Stack>
            )}

            {isMobile && focus && !editOpen && !newJobStep && !groupsOpen ? (
                <Portal>
                    <Paper
                        elevation={12}
                        sx={(theme) => ({
                            position: "fixed",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: theme.zIndex.appBar,
                            px: 1.5,
                            pt: 1.25,
                            pb: "calc(12px + env(safe-area-inset-bottom))",
                            borderTop: `1px solid ${theme.palette.divider}`,
                            bgcolor: theme.palette.background.paper,
                            backgroundImage: "none",
                        })}
                    >
                        <Stack direction="row" spacing={1}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<EditIcon />}
                                onClick={() => beginEdit(focus)}
                                sx={{ minHeight: 44 }}
                            >
                                Edit
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<RequestQuoteIcon />}
                                onClick={buildQuoteFromFocus}
                                disabled={!canBuildQuote}
                                sx={{ minHeight: 44 }}
                            >
                                Quote
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<AddIcon />}
                                disabled={quoteLocked}
                                onClick={(e) => setAddAnchor(e.currentTarget)}
                                sx={{ minHeight: 44 }}
                            >
                                Add child
                            </Button>
                        </Stack>
                    </Paper>
                </Portal>
            ) : null}

            {editDialog}
            {moreMenu}
            {addMenu}

            <Dialog open={Boolean(newJobStep)} onClose={() => setNewJobStep("")} fullWidth maxWidth="sm" fullScreen={isMobile}>
                {isMobile ? (
                    <AppBar sx={{ position: "relative" }} color="transparent" elevation={0}>
                        <Toolbar sx={{ borderBottom: 1, borderColor: "divider" }}>
                            <IconButton edge="start" onClick={() => setNewJobStep("")} aria-label="Close">
                                <CloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 1, fontWeight: 800 }}>
                                {newJobStep === "scope"
                                    ? "Who is this for?"
                                    : newJobStep === "customer"
                                        ? "Link a customer"
                                        : "New job"}
                            </Typography>
                        </Toolbar>
                    </AppBar>
                ) : (
                    <DialogTitle>
                        {newJobStep === "scope"
                            ? "Who is this for?"
                            : newJobStep === "customer"
                                ? "Link a customer"
                                : "New job"}
                    </DialogTitle>
                )}
                <DialogContent dividers>
                    {newJobStep === "scope" ? (
                        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                            <Button
                                variant="outlined"
                                fullWidth
                                sx={{ justifyContent: "flex-start", textAlign: "left", py: 1.5, minHeight: 64 }}
                                onClick={() => startPlan({ scope: "general" })}
                            >
                                <Box>
                                    <Typography fontWeight={700}>General</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Stays on your list. Not tied to a customer.
                                    </Typography>
                                </Box>
                            </Button>
                            <Button
                                variant="outlined"
                                fullWidth
                                sx={{ justifyContent: "flex-start", textAlign: "left", py: 1.5, minHeight: 64 }}
                                onClick={() => setNewJobStep("customer")}
                            >
                                <Box>
                                    <Typography fontWeight={700}>Customer</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Linked to an account so it stays with that relationship.
                                    </Typography>
                                </Box>
                            </Button>
                        </Stack>
                    ) : null}
                    {newJobStep === "customer" ? (
                        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                            <TextField
                                size="small"
                                fullWidth
                                autoFocus={!isMobile}
                                value={customerQuery}
                                onChange={(e) => setCustomerQuery(e.target.value)}
                                placeholder="Search customers"
                            />
                            <List dense sx={{ maxHeight: { xs: "none", sm: 360 }, overflow: "auto" }}>
                                {filteredCustomers.length ? (
                                    filteredCustomers.map((customer) => (
                                        <ListItemButton
                                            key={customer.id}
                                            onClick={() => startPlan({ scope: "customer", customer })}
                                            sx={{ minHeight: 52 }}
                                        >
                                            <ListItemText
                                                primary={customer.name}
                                                secondary={[customer.company, customer.email].filter(Boolean).join(" · ")}
                                            />
                                        </ListItemButton>
                                    ))
                                ) : (
                                    <Typography color="text.secondary" sx={{ p: 1 }}>
                                        No customers match that search.
                                    </Typography>
                                )}
                            </List>
                        </Stack>
                    ) : null}
                </DialogContent>
                {isMobile ? null : (
                    <DialogActions>
                        <Button onClick={() => setNewJobStep("")}>Cancel</Button>
                    </DialogActions>
                )}
            </Dialog>

            <Dialog open={groupsOpen} onClose={() => !busy && setGroupsOpen(false)} fullWidth maxWidth="md" fullScreen={isMobile}>
                {isMobile ? (
                    <AppBar sx={{ position: "relative" }} color="transparent" elevation={0}>
                        <Toolbar sx={{ borderBottom: 1, borderColor: "divider" }}>
                            <IconButton edge="start" onClick={() => setGroupsOpen(false)} aria-label="Close">
                                <CloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 1, flex: 1, fontWeight: 800 }}>Manage groups</Typography>
                            <Button variant="contained" disabled={busy || !groupDraft} onClick={saveGroup}>
                                {groupDraft?.id ? "Save" : "Create"}
                            </Button>
                        </Toolbar>
                    </AppBar>
                ) : (
                    <DialogTitle>Manage groups</DialogTitle>
                )}
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        These labels tag components and purchases (robot cell, EOAT, software, etc.). They live in PBR Core —
                        add and edit here anytime. Import from Wix only copies jobs once; new groups you create stay local.
                    </Typography>
                    {isMobile ? (
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                            <Button size="small" disabled={busy} onClick={() => setGroupDraft(blankGroup())}>
                                New group
                            </Button>
                            <Button size="small" disabled={busy} onClick={seedGroups}>
                                Seed defaults
                            </Button>
                            {groupDraft?.id ? (
                                <Button
                                    size="small"
                                    color="error"
                                    disabled={busy}
                                    onClick={() =>
                                        setConfirm({
                                            kind: "group",
                                            id: groupDraft.id,
                                            title: `Delete ${groupDraft.label}?`,
                                            body: "Only groups with no active items can be deleted. Archive instead if jobs still use this key.",
                                        })
                                    }
                                >
                                    Delete
                                </Button>
                            ) : null}
                        </Stack>
                    ) : null}
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        <Paper variant="outlined" sx={{ width: { xs: "100%", md: 280 }, flexShrink: 0, maxHeight: { xs: 220, md: 440 }, overflow: "auto" }}>
                            <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
                                <Typography variant="overline" color="text.secondary">
                                    {groups.length} group{groups.length === 1 ? "" : "s"}
                                </Typography>
                            </Box>
                            <List dense disablePadding>
                                {groups
                                    .slice()
                                    .sort((a, b) => String(a.sortOrder || "").localeCompare(String(b.sortOrder || "")))
                                    .map((group) => (
                                        <ListItemButton
                                            key={group.id}
                                            selected={groupDraft?.id === group.id}
                                            onClick={() => setGroupDraft({ ...group })}
                                            sx={{
                                                bgcolor: groupDraft?.id === group.id ? alpha(theme.palette.primary.main, 0.12) : undefined,
                                            }}
                                        >
                                            <ListItemText
                                                primary={`${group.icon || "◎"} ${group.label}`}
                                                secondary={
                                                    group.archived
                                                        ? `Archived · ${group.key}`
                                                        : group.blurb || group.key
                                                }
                                            />
                                        </ListItemButton>
                                    ))}
                            </List>
                        </Paper>
                        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                            {groupDraft ? (
                                <>
                                    <TextField
                                        label="Label"
                                        size="small"
                                        fullWidth
                                        value={groupDraft.label || ""}
                                        onChange={(e) => setGroupDraft({ ...groupDraft, label: e.target.value })}
                                        placeholder="Machine tending"
                                        helperText={
                                            groupDraft.id
                                                ? `Stable key: ${groupDraft.key}`
                                                : "Key is generated from the label on create."
                                        }
                                    />
                                    <TextField
                                        label="Blurb"
                                        size="small"
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        value={groupDraft.blurb || ""}
                                        onChange={(e) => setGroupDraft({ ...groupDraft, blurb: e.target.value })}
                                        placeholder="Short description shown in the picker"
                                    />
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                                        <TextField
                                            label="Icon"
                                            size="small"
                                            value={groupDraft.icon || ""}
                                            onChange={(e) => setGroupDraft({ ...groupDraft, icon: e.target.value })}
                                            inputProps={{ maxLength: 4 }}
                                            sx={{ width: { xs: "100%", sm: 100 } }}
                                        />
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Tone</InputLabel>
                                            <Select
                                                label="Tone"
                                                value={groupDraft.tone || "lime"}
                                                onChange={(e) => setGroupDraft({ ...groupDraft, tone: e.target.value })}
                                            >
                                                {PLANNER_TONES.map((tone) => (
                                                    <MenuItem key={tone} value={tone}>
                                                        {tone}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            label="Sort"
                                            size="small"
                                            value={groupDraft.sortOrder || ""}
                                            onChange={(e) => setGroupDraft({ ...groupDraft, sortOrder: e.target.value })}
                                            placeholder="0019"
                                            helperText="Lower sorts first"
                                            sx={{ width: { xs: "100%", sm: 120 } }}
                                        />
                                    </Stack>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(groupDraft.archived)}
                                                onChange={(e) =>
                                                    setGroupDraft({ ...groupDraft, archived: e.target.checked })
                                                }
                                            />
                                        }
                                        label="Hide from pickers (archive)"
                                    />
                                </>
                            ) : (
                                <Typography color="text.secondary">Select a group or create a new one.</Typography>
                            )}
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap", gap: 1, display: isMobile ? "none" : "flex" }}>
                    <Button disabled={busy} onClick={() => setGroupDraft(blankGroup())}>
                        New group
                    </Button>
                    <Button disabled={busy} onClick={seedGroups}>
                        Seed defaults
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    {groupDraft?.id ? (
                        <Button
                            color="error"
                            disabled={busy}
                            onClick={() =>
                                setConfirm({
                                    kind: "group",
                                    id: groupDraft.id,
                                    title: `Delete ${groupDraft.label}?`,
                                    body: "Only groups with no active items can be deleted. Archive instead if jobs still use this key.",
                                })
                            }
                        >
                            Delete
                        </Button>
                    ) : null}
                    <Button onClick={() => setGroupsOpen(false)}>Close</Button>
                    <Button variant="contained" disabled={busy || !groupDraft} onClick={saveGroup}>
                        {groupDraft?.id ? "Save group" : "Create group"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(confirm)} onClose={() => !busy && setConfirm(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{confirm?.title}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        {confirm?.body}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button disabled={busy} onClick={() => setConfirm(null)}>
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" disabled={busy} onClick={runConfirm}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <LogJobActionDialog
                open={logOpen}
                job={job}
                items={job ? items.filter((row) => row.id !== job.id && rootIdOf(row, byId) === job.id) : []}
                initialKind={logKind}
                initialItemId={logItemId}
                busy={busy}
                onClose={() => setLogOpen(false)}
                onSave={saveLog}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
